import type { PositionType } from '../trading/types';

import type { Level } from './levels';

/**
 * Whether the trader's stop sits where the market has turned before.
 *
 * The classic beginner mistake: a long's stop parked ON a support. That is
 * exactly where everyone else's stops are, and the wick that tests the level
 * takes them all before price turns back up. The fix is a stop just PAST the
 * level, so the test does not reach it.
 *
 * Only the stop is checked. A take-profit verdict would be predicting where
 * price will not go, which is advice however carefully it is worded — and the
 * ROI presets put a take-profit wherever the leverage lands it, usually
 * nowhere near a level.
 */

export type StopCheckKind = 'onLevel' | 'nearLevel' | 'clear';

export interface StopCheck {
    kind: StopCheckKind;
    level: Level | null;
    /** distance from the stop to the level's price, in % of spot */
    gapPct: number | null;
    /** a stop just past the level on the losing side */
    suggestedStop: number | null;
}

/** How close, in % of spot, counts as "the wick that tests it reaches you". */
export const NEAR_PCT = 0.35;
/** How far past the level the suggested stop sits, in % of spot. */
const PAST_PCT = 0.2;

export function checkStop(
    trade: { type: PositionType; stopLossPrice: number | null },
    levels: readonly Level[],
    spot: number,
    nearPct = NEAR_PCT,
): StopCheck {
    const clear: StopCheck = {
        kind: 'clear',
        level: null,
        gapPct: null,
        suggestedStop: null,
    };
    const stop = trade.stopLossPrice;
    if (stop === null || !Number.isFinite(stop) || stop <= 0) return clear;
    if (!Number.isFinite(spot) || spot <= 0) return clear;
    // a long is hurt by price falling into a support; a short by rising into a resistance
    const kind = trade.type === 'LONG' ? 'support' : 'resistance';
    const candidates = levels
        .filter((l) => l.kind === kind)
        .sort((a, b) => b.strength - a.strength);
    const pad = spot * (PAST_PCT / 100);
    for (const level of candidates) {
        const gapPct = (Math.abs(stop - level.price) / spot) * 100;
        const inside = stop >= level.low && stop <= level.high;
        if (inside || gapPct <= nearPct) {
            return {
                kind: inside ? 'onLevel' : 'nearLevel',
                level,
                gapPct: Number(gapPct.toFixed(2)),
                suggestedStop:
                    trade.type === 'LONG' ? level.low - pad : level.high + pad,
            };
        }
    }
    return clear;
}
